/**
 * D1 — Recipient list validation preview.
 *
 * Calls ``POST /api/campaigns/recipients/validate`` with the parsed
 * recipient list + trunk_id (for region check) and renders the
 * server-side eligibility breakdown:
 *   - valid: E.164-parseable + not on the global DNC + tenant
 *     suppression list clean + quiet-hour eligible at dispatch time
 *   - invalid: parse failure (non-E.164), DNC hit, suppression hit
 *   - deferred: quiet-hour deferral (still eligible; will fire later)
 *
 * The preview also surfaces:
 *   - Estimated total cost (recipients × est-avg-duration × trunk rate)
 *   - Estimated dispatch window (recipients ÷ trunk_max_cps)
 *   - Per-recipient region split (with caller-id region mismatch warning)
 */
import React, { useState, useEffect } from "react";
import { COLORS } from "../../../theme/colors";
import { Badge, Button } from "../../../components/ui";
import { campaignApi } from "../../../api/client";

const RESULT_COLORS = {
  valid: COLORS.green,
  invalid: COLORS.pink,
  dnc: COLORS.pink,
  suppressed: COLORS.orange,
  deferred_quiet_hours: COLORS.orange,
  parse_error: COLORS.pink,
};

// FCOV: pure derivation of the count-chip numbers from the validate
// response. Extracted (and exported) so the count logic can be unit-tested
// without driving the debounced fetch; wired into the render below.
export function deriveRecipientCounts(result) {
  const counts = (result && result.counts) || {};
  return {
    validCount: counts.valid || 0,
    invalidCount: counts.invalid || 0,
    deferredCount: counts.deferred_quiet_hours || 0,
    dncCount: counts.dnc || 0,
    suppressedCount: counts.suppressed || 0,
  };
}

export default function RecipientListPreview({ recipients, trunkId, onValidated }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const recipientHash = JSON.stringify(recipients);

  // D13 — 500ms debounce + cancel-in-flight. The recipient input is
  // a textarea; pre-fix every keystroke fired a validateRecipients
  // request. With a 500-line paste the user could rack up >100
  // in-flight requests; the last one to land wins, others race + leak.
  // Now: wait until typing pauses for 500ms, then fire ONE request;
  // a new keystroke before the timer fires reschedules; a new
  // keystroke after fires triggers the previous request's
  // cancellation flag (the `cancelled` closure inside the timer).
  useEffect(() => {
    if (!recipients || recipients.length === 0 || !trunkId) {
      setResult(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      setLoading(true);
      setError(null);
      (async () => {
        try {
          const res = await campaignApi.validateRecipients({ recipients, trunk_id: trunkId });
          if (cancelled) return;
          const data = res?.data || res;
          setResult(data);
          if (onValidated) onValidated(data);
        } catch (e) {
          if (!cancelled) setError(e.message || String(e));
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [recipientHash, trunkId]);

  if (!recipients || recipients.length === 0) return null;
  if (!trunkId) {
    return (
      <div style={{ padding: "10px 12px", borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, fontSize: 12, color: COLORS.textMuted }}>
        Select a trunk above to validate recipient eligibility.
      </div>
    );
  }
  if (loading) {
    return (
      <div style={{ padding: "10px 12px", borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, fontSize: 12, color: COLORS.textMuted }}>
        Validating {recipients.length} recipients…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: "10px 12px", borderRadius: 8, background: `${COLORS.pink}12`, border: `1px solid ${COLORS.pink}40`, fontSize: 12, color: COLORS.pink }}>
        Recipient validation failed: {error}
      </div>
    );
  }
  if (!result) return null;

  const items = result.items || [];
  const { validCount, invalidCount, deferredCount, dncCount, suppressedCount } =
    deriveRecipientCounts(result);

  return (
    <div style={{ padding: 14, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <strong style={{ fontSize: 13 }}>Recipient validation</strong>
        <Button size="sm" variant="ghost" onClick={() => setExpanded(e => !e)}>
          {expanded ? "Hide details" : `Show ${items.length} rows`}
        </Button>
      </div>

      {/* Count chips */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <Badge color={COLORS.green}>{validCount} ready</Badge>
        {deferredCount > 0 && <Badge color={COLORS.orange}>{deferredCount} deferred (quiet hours)</Badge>}
        {dncCount > 0 && <Badge color={COLORS.pink}>{dncCount} DNC</Badge>}
        {suppressedCount > 0 && <Badge color={COLORS.orange}>{suppressedCount} suppressed</Badge>}
        {invalidCount > 0 && <Badge color={COLORS.pink}>{invalidCount} invalid</Badge>}
      </div>

      {/* Cost + dispatch window estimates */}
      {result.estimates && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, padding: "8px 0", borderTop: `1px solid ${COLORS.border}`, marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 10, color: COLORS.textDim }}>Estimated cost</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>
              ${(result.estimates.total_cost_usd || 0).toFixed(2)}
            </div>
            <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
              {validCount} × ~{result.estimates.avg_duration_seconds || 90}s @ ${result.estimates.rate_per_minute_usd}/min
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: COLORS.textDim }}>Estimated dispatch window</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>
              ~{result.estimates.dispatch_window_minutes || "?"} min
            </div>
            <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
              at trunk max {result.estimates.max_cps} CPS, {result.estimates.max_concurrent} concurrent
            </div>
          </div>
        </div>
      )}

      {/* Region mismatch warning */}
      {result.warnings && result.warnings.length > 0 && (
        <div style={{ padding: "8px 10px", borderRadius: 6, background: `${COLORS.orange}12`, border: `1px solid ${COLORS.orange}40`, fontSize: 11, color: COLORS.orange, marginBottom: 8 }}>
          {result.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
        </div>
      )}

      {/* Detail rows (expanded) */}
      {expanded && items.length > 0 && (
        <div style={{ maxHeight: 240, overflowY: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 6 }}>
          {items.map((it, i) => (
            <div key={i} style={{
              padding: "6px 10px",
              borderBottom: i < items.length - 1 ? `1px solid ${COLORS.border}` : "none",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              fontSize: 11,
              background: it.eligible ? "transparent" : `${COLORS.pink}05`,
            }}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontFamily: "monospace", color: COLORS.text }}>{it.handle}</span>
                {it.name && <span style={{ color: COLORS.textMuted, fontSize: 10 }}>{it.name}</span>}
              </div>
              <Badge color={RESULT_COLORS[it.status] || COLORS.textMuted}>
                {it.status}{it.reason ? ` · ${it.reason}` : ""}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
